import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFormStepper from './MobileFormStepper';
import Card2 from '../../../components/Card2';
import FormStepper from './FormStepper';
import FormGenerator from './FormGenerator';
import { HiExclamation } from 'react-icons/hi';
import Modal from '../../../components/Modal';
import UndergradEducationalInfoForm from './UnderGradEducationalInfoForm';
import ProgramInfoForm from './ProgramInfoForm';
import PostgradEducationalInfoForm from './PostGradEducationalInfoForm';
import WorkStatusForm from './WorkStatusForm';

const stepsData:any = {
    undergrad: [
        { title: 'Age Requirement', description: 'The age range that candidates must fall into to be eligible.' },
        { title: 'Educational Background', description: 'The necessary educational qualifications required for eligibility' },
        { title: 'Program Information', description: 'The academic achievements or grades required for eligibility' }
    ],
    postgrad: [
        { title: 'Age Requirement', description: 'The age range that candidates must fall into to be eligible.' },
        { title: 'Educational Background', description: 'The necessary educational qualifications required for eligibility' },
        { title: 'Work Status', description: 'Let us in on your work status' },
        { title: 'Program Information', description: 'The academic achievements or grades required for eligibility' }
    ]
};

const FormProcessingSteps = () => {
    const navigate = useNavigate();
    const [state, setState] = useState({
        activeStep: 0,
        category: '',
        age: 0,
        steps: [{ title: 'Category', description: 'Select a category and see if you qualify' }],
        isContinuingStudent: false,
        gradeType: 'CWA',
        grade: 0,
        academicLevel: 0,
        workingStatusId: 0,
        salary: 0,
        showEligibilityModal: false,
        showSuccessModal: false
    });

    const { activeStep, category, age, steps, isContinuingStudent, gradeType, grade, academicLevel, workingStatusId, salary, showEligibilityModal, showSuccessModal } = state;

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showSuccessModal) {
            timer = setTimeout(() => {
                navigate('/auth/signup');
            }, 5000); // Redirect after 5 seconds
        }
        return () => clearTimeout(timer); // Cleanup on unmount or when showSuccessModal changes
    }, [showSuccessModal, navigate]);

    const closeModal = () => setState(prev => ({ ...prev, showEligibilityModal: false, showSuccessModal: false }));

    const handleNextStep = () => {
        if (activeStep === 0 && !category) return;

        if (activeStep === 0) {
            setState(prev => ({
                ...prev,
                steps: [{ title: 'Category', description: 'Select a category and see if you qualify' }, ...stepsData[category]]
            }));
        }
        setState(prev => ({ ...prev, activeStep: prev.activeStep + 1 }));
    };

    const handlePreviousStep = () => {
        setState(prev => ({ ...prev, activeStep: prev.activeStep - 1 }));
    };

    const validateEligibility = () => {
        if (age < 16) return false;

        if (category === 'undergrad') {
            if (isContinuingStudent) {
                return gradeType === 'CWA' ? grade >= 70 : grade >= 3.0;
            } 
            return grade <= 18;
        } 

        if (category === 'postgrad') {
            return academicLevel >= 4 && (gradeType === 'CWA' ? grade >= 70 : grade >= 3.0) && workingStatusId <= 6 && salary >= 1500;
        }

        return true;
    };

    const handleEducationInfo = (val:any, type:string) => {
        if (type === 'undergrad') {
            setState(prev => ({ ...prev, ...val}));
        } else {
            setState(prev => ({ ...prev, ...val }));
        }
    };

    const handleWorkStatusInfo = (val:any) => {
        setState(prev => ({ ...prev, ...val}));
    };

    const onSubmit = () => {
        if (validateEligibility()) {
            setState(prev => ({ ...prev, showSuccessModal: true }));
        } else {
            setState(prev => ({ ...prev, showEligibilityModal: true }));
        }
    };

    return (
        <div className='flex flex-col items-center justify-center h-full w-full'>
            <div className='w-full sm:hidden'>
                <MobileFormStepper steps={steps} activeStep={activeStep} />
            </div>
            <div className={`h-[95%] w-[95%] md:w-[60%]`}>
                <Card2
                    leftContent={<FormStepper steps={steps} activeStep={activeStep} />}
                    rightContent={
                        <div className='w-full h-full flex flex-col items-center justify-center px-2 md:px-10'>
                            {activeStep === 0 && (
                                <div className="h-full w-full flex flex-col justify-center">
                                    <FormGenerator
                                        title='Category'
                                        label='Select Category'
                                        type='select'
                                        onChange={val => setState(prev => ({ ...prev, category: val }))}
                                        options={[
                                            { label: 'Under Graduate', value: 'undergrad' },
                                            { label: 'Post Graduate', value: 'postgrad' },
                                        ]}
                                    />
                                </div>
                            )}
                            {activeStep === 1 && (
                                <div className="h-full w-full flex flex-col justify-center">
                                    <FormGenerator
                                        title='Age Requirement'
                                        label='Enter Your Age'
                                        type='number'
                                        onChange={val => setState(prev => ({ ...prev, age: val }))}
                                    />
                                </div>
                            )}

                            {activeStep === 2 && category === 'undergrad' && (
                                <UndergradEducationalInfoForm onChange={val => handleEducationInfo(val, 'undergrad')} />
                            )}
                            {activeStep === 2 && category === 'postgrad' && (
                                <PostgradEducationalInfoForm onChange={val => handleEducationInfo(val, 'postgrad')} />
                            )}
                            {activeStep === 3 && category === 'postgrad' && (
                                <WorkStatusForm onChange={handleWorkStatusInfo} />
                            )}
                            {activeStep > 0 && activeStep === steps.length - 1 && (
                                <ProgramInfoForm />
                            )}
                            <div className='w-full flex justify-between mt-4 gap-5 mb-10 md:mb-0'>
                                {activeStep > 0 && (
                                    <button
                                        onClick={handlePreviousStep}
                                        className='border border-primary text-black py-2 px-4 rounded-lg hover:border-yellow-700 transition duration-300 w-full'
                                    >
                                        Previous
                                    </button>
                                )}
                                {activeStep === 0 || activeStep < steps.length - 1 ? (
                                    <button
                                        onClick={handleNextStep}
                                        className='bg-primary text-black py-2 px-4 rounded-lg hover:bg-yellow-600 transition duration-300 w-full'
                                    >
                                        Next
                                    </button>
                                ) : (
                                    <button
                                        onClick={onSubmit}
                                        className='bg-primary text-black py-2 px-4 rounded-lg hover:bg-yellow-600 transition duration-300 w-full'
                                    >
                                        Submit
                                    </button>
                                )}
                            </div>
                        </div>
                    }
                />
            </div>

            {showEligibilityModal && (
                <Modal onClose={closeModal} title="Ineligibility Notice">
                    <div className="py-4">
                        <HiExclamation className="text-red-500 text-4xl mb-4" />
                        <p className="text-gray-700">
                            Unfortunately, you do not meet the eligibility criteria for the selected category.
                            Please review the requirements and try again.
                        </p>
                    </div>
                </Modal>
            )}

            {showSuccessModal && (
                <Modal onClose={() => navigate('/auth/signup')} buttonText='Proceed' title="Success">
                    <div className="py-4">
                        <p className="text-gray-700">
                            Congratulations! You are eligible. You will be redirected to the signup page shortly.
                        </p>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default FormProcessingSteps;
